import {
  createInitialPumpState,
  executePumpTradeIntent,
  lamportsToSol,
  marketCapSolFromState,
  rawToTokenUi,
  candlesFromMarketCapPrints,
  type PumpBondingState,
  type SimPrint,
  type SimCandle,
} from "@/lib/algo/pumpSimulationMath";
import type { Choreography, WalletLedger, TapeAction } from "./choreography";

export const PROTOCOL_FEE_BPS = 95;
export const CREATOR_FEE_BPS = 30;

export type StreamingCtx = {
  state: PumpBondingState;
  ledgers: Map<string, WalletLedger>;
  prints: SimPrint[];
  candles: SimCandle[];
  totalIn: number;
  totalOut: number;
  solUsd: number;
};

export class StreamingSimulator {
  ctx: StreamingCtx;
  macroRules: TapeAction[];
  loopIntervals: Map<string, number>;
  virtualTimeMs: number = 0;
  bucketMs: number;
  
  constructor(choreo: Choreography) {
    this.ctx = {
      state: createInitialPumpState(),
      ledgers: new Map(choreo.wallets.map((w) => [w.id, this.emptyLedger(w.id)])),
      prints: [],
      candles: [],
      totalIn: 0,
      totalOut: 0,
      solUsd: choreo.solUsd > 0 ? choreo.solUsd : 0,
    };
    
    if (choreo.devBuySol > 0 && !this.ctx.ledgers.has("creator")) {
      this.ctx.ledgers.set("creator", { walletId: "creator", cashSol: choreo.devBuySol, tokens: 0, boughtTokens: 0, costBasisSol: 0, realizedSol: 0, buys: 0, sells: 0 });
    }

    this.bucketMs = choreo.bucketMs > 0 ? choreo.bucketMs : 1000;
    
    // We only take the BASE actions (t <= loopInterval or within the first cycle) 
    // from the choreo tape. We assume graph.ts `buildTape` is STILL returning 
    // the unrolled 5-minute tape. To fix this, we should actually reconstruct 
    // the macros, but for now we can just use the provided tape up to the first loop.
    this.macroRules = [...(choreo.tape ?? [])].sort((a,b) => a.t - b.t);
    this.loopIntervals = new Map(choreo.wallets.map(w => [w.id, w.loopIntervalMs ?? 0]));
  }

  emptyLedger(id: string): WalletLedger {
    return { walletId: id, cashSol: 0, tokens: 0, boughtTokens: 0, costBasisSol: 0, realizedSol: 0, buys: 0, sells: 0 };
  }

  /** Advances the engine by deltaTimeMs and executes any trades that land in this window */
  tick(deltaTimeMs: number): { newPrints: SimPrint[]; newCandles: SimCandle[] } {
    const startMs = this.virtualTimeMs;
    this.virtualTimeMs += deltaTimeMs;
    const endMs = this.virtualTimeMs;

    const printsBefore = this.ctx.prints.length;

    // A true streaming engine should yield trades based on macros and loopIntervals.
    // Since macroRules contains absolute actions, we evaluate those that fall between startMs and endMs.
    // For infinite looping, we also evaluate trades that modulo into this window.
    
    for (const rule of this.macroRules) {
      const interval = this.loopIntervals.get(rule.walletId) || 0;
      
      // If no interval, it only executes once at rule.t
      if (interval === 0) {
        if (rule.t >= startMs && rule.t < endMs) {
          this.executeRule(rule, rule.t);
        }
      } else {
        // It loops every `interval`. Does a multiple of `interval` + rule.t fall in [startMs, endMs)?
        // e.g., t = 5000, interval = 10000. It executes at 5000, 15000, 25000.
        // We find the next execution after startMs.
        if (startMs >= rule.t) {
          const cyclesPassed = Math.floor((startMs - rule.t) / interval);
          const nextExecutionTime = rule.t + (cyclesPassed + 1) * interval;
          if (nextExecutionTime >= startMs && nextExecutionTime < endMs) {
            this.executeRule(rule, nextExecutionTime);
          }
        } else {
           if (rule.t >= startMs && rule.t < endMs) {
             this.executeRule(rule, rule.t);
           }
        }
      }
    }

    const newPrints = this.ctx.prints.slice(printsBefore);
    
    // Incrementally build candles. A simple approach is just re-running candle generation 
    // on the prints, but optimized for streaming, we could just return the delta.
    // For now we just return the full array to replace the chart data.
    this.ctx.candles = candlesFromMarketCapPrints(this.ctx.prints, this.bucketMs);
    
    return { newPrints, newCandles: this.ctx.candles };
  }

  executeRule(a: TapeAction, executionTimeMs: number) {
    if (a.kind === "buy") this.doBuy(a.walletId, a.sol ?? 0, executionTimeMs, a.label);
    else if (a.kind === "sell") this.doSell(a.walletId, a.pct ?? 0, executionTimeMs, a.label);
    else if (a.kind === "rotate") {
      const sold = this.doSell(a.walletId, a.sellPct ?? 100, executionTimeMs, a.label);
      if (sold && sold.walletSol > 0) {
        const to = this.ctx.ledgers.get(a.toWalletId ?? "");
        if (to) { 
          to.cashSol += sold.walletSol; 
          this.doBuy(a.toWalletId!, sold.walletSol, executionTimeMs, a.toTrader ?? a.label); 
        }
      }
    }
  }

  doBuy(walletId: string, maxSpendSol: number, ts: number, label: string) {
    const ledger = this.ctx.ledgers.get(walletId);
    if (!ledger) return;
    if (maxSpendSol <= 0) return;

    const exec = executePumpTradeIntent(this.ctx.state, {
      side: "buy", sol: maxSpendSol, buySolMode: "wallet-gross",
      protocolFeeBps: PROTOCOL_FEE_BPS, creatorFeeBps: CREATOR_FEE_BPS,
    });
    const walletSol = lamportsToSol(exec.walletSolLamports);
    const curveSol = lamportsToSol(exec.curveSolLamports);
    const tokensBought = rawToTokenUi(exec.tokenRaw);
    
    this.ctx.state = exec.next;
    ledger.cashSol = Math.max(0, ledger.cashSol - walletSol);
    ledger.tokens += tokensBought;
    ledger.boughtTokens += tokensBought;
    ledger.costBasisSol += walletSol;
    ledger.buys += 1;
    this.ctx.totalIn += walletSol;
    
    this.recordPrint(ts, "buy", walletSol, curveSol, tokensBought, label);
  }

  doSell(walletId: string, pct: number, ts: number, label: string) {
    const ledger = this.ctx.ledgers.get(walletId);
    if (!ledger) return;
    const tokensToSell = ledger.tokens * (Math.max(0, Math.min(100, pct)) / 100);
    if (!(tokensToSell > 0)) return;
    
    const exec = executePumpTradeIntent(this.ctx.state, {
      side: "sell", tokenAmount: tokensToSell,
      protocolFeeBps: PROTOCOL_FEE_BPS, creatorFeeBps: CREATOR_FEE_BPS,
    });
    const walletSol = lamportsToSol(exec.walletSolLamports);
    const curveSol = lamportsToSol(exec.curveSolLamports);
    const tokensSold = rawToTokenUi(exec.tokenRaw);
    
    this.ctx.state = exec.next;
    ledger.cashSol += walletSol;
    ledger.tokens = Math.max(0, ledger.tokens - tokensSold);
    ledger.realizedSol += walletSol;
    ledger.sells += 1;
    this.ctx.totalOut += walletSol;
    
    this.recordPrint(ts, "sell", walletSol, curveSol, tokensSold, label);
    return { walletSol, tokensSold };
  }

  recordPrint(ts: number, side: "buy" | "sell", walletSol: number, curveSol: number, tokenAmount: number, label: string) {
    const marketCapSol = marketCapSolFromState(this.ctx.state);
    this.ctx.prints.push({
      ts, side, walletSol, sol: walletSol, curveSol,
      tokenAmount, marketCapSol, marketCapUsd: marketCapSol * this.ctx.solUsd,
      state: { ...this.ctx.state }, label
    });
  }
}
